// datesHelper.ts
export type DeliveryRules = {
  cutoffHours: number;
  allowedWeekdays: number[]; // 0=Sun..6=Sat
  blockedYmd: Set<string>;
  neverAllowToday: boolean;
};

/** @deprecated Use Qivora API available-dates response instead of hardcoded rules. */
export const DEFAULT_RULES: DeliveryRules = {
  cutoffHours: 36,
  allowedWeekdays: [1, 2, 3, 4, 5], // Mon-Fri fallback — real rules come from Qivora API
  blockedYmd: new Set<string>(),
  neverAllowToday: true,
};

export function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function cutoffForDeliveryDate(deliveryDate: Date, cutoffHours = 36) {
  const deliveryStart = startOfDay(deliveryDate);
  const cutoff = new Date(deliveryStart);
  cutoff.setHours(cutoff.getHours() - cutoffHours);
  return cutoff;
}

export function toYmd(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function fromYmd(s?: string) {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export function computeShipDate(delivery: Date) {
  const d = new Date(delivery);
  d.setDate(d.getDate() - 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function formatHuman(date: Date) {
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function isAllowedWeekday(date: Date, rules: DeliveryRules = DEFAULT_RULES) {
  const day = date.getDay();
  return rules.allowedWeekdays.includes(day);
}

/**
 * Pure function: no hooks, no component state.
 * Pass in `now` (client time) so it can be reused in FE + server validation later.
 */
export function isDisabledDate(
  date: Date,
  now: Date,
  rules: DeliveryRules = DEFAULT_RULES
) {
  const d = startOfDay(date);
  const todayStart = startOfDay(now);

  if (rules.neverAllowToday && d.getTime() === todayStart.getTime()) return true;
  if (d < todayStart) return true;
  if (!isAllowedWeekday(d, rules)) return true;
  if (rules.blockedYmd.has(toYmd(d))) return true;

  const cutoff = cutoffForDeliveryDate(d, rules.cutoffHours);
  if (now >= cutoff) return true;

  return false;
}

export function findNextAllowedDate(
  now: Date,
  rules: DeliveryRules = DEFAULT_RULES,
  daysToSearch = 370
) {
  const todayStart = startOfDay(now);
  const d = new Date(todayStart);
  d.setDate(d.getDate() + 1); // start from tomorrow

  for (let i = 0; i < daysToSearch; i++) {
    const candidate = startOfDay(d);
    if (!isDisabledDate(candidate, now, rules)) return candidate;
    d.setDate(d.getDate() + 1);
  }
  return undefined;
}

// --- Qivora API-backed helpers ---

import type { QivoraDate } from "@/api/qivoraClient";

/** Build a Set<string> of YYYY-MM-DD from Qivora dates for O(1) lookup */
export function buildAllowedDateSet(dates: QivoraDate[]): Set<string> {
  return new Set(dates.map((d) => d.date));
}

/** Build a map from delivery date -> ship date for quick lookup */
export function buildShipDateMap(dates: QivoraDate[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const d of dates) m.set(d.date, d.ship_date);
  return m;
}

/** Look up ship date from API response */
export function lookupShipDate(dates: QivoraDate[], ymd: string): string | undefined {
  return dates.find((d) => d.date === ymd)?.ship_date;
}

/** Get the first available date from the API response */
export function findFirstAvailableFromApi(dates: QivoraDate[]): QivoraDate | undefined {
  return dates[0];
}
